import { Settings } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListSubheader,
  Popover,
  Tooltip,
  Typography,
  Button,
  Divider,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import React from 'react';
import { alpha } from '@mui/material/styles';
import { tokens } from '../../../mui-theme';

const TableColumnManager = ({
  allHeaders,
  selectedKeys,
  onSelectionChange,
  mandatoryKeys,
  defaultKeys,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleToggle = (key) => {
    onSelectionChange((prev) =>
      prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]
    );
  };

  const handleReset = () => {
    if (defaultKeys) {
      onSelectionChange(defaultKeys);
    }
  };

  const open = Boolean(anchorEl);
  const visibleCount = selectedKeys?.length ?? 0;
  const totalCount = allHeaders?.length ?? 0;

  return (
    <>
      <Tooltip title="Configure columns" arrow>
        <IconButton
          onClick={handleOpen}
          disableRipple
          aria-label="configure columns"
          sx={{
            height: 36,
            width: 36,
            border: '1px solid',
            borderColor: 'divider',
            color: 'text.secondary',
            backgroundColor: 'background.paper',
            transition: 'all 0.15s ease',
            borderRadius: 1, // Match theme sm
            '&:hover': {
              borderColor: 'primary.main',
              color: 'primary.main',
              backgroundColor: alpha(tokens.colors.primary[500], 0.05),
            },
          }}
        >
          <Settings sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ width: 240 }}>
          <List
            dense
            disablePadding
            subheader={
              <ListSubheader
                disableSticky={false}
                sx={{
                  px: 2,
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  lineHeight: 'unset',
                }}
              >
                <Typography variant="overline" color="text.secondary">
                  Columns
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {visibleCount}/{totalCount} visible
                </Typography>
              </ListSubheader>
            }
            sx={{ maxHeight: 320, overflow: 'auto' }}
          >
            {allHeaders?.map((header) => {
              const isDisabled = mandatoryKeys?.includes(header.key);

              return (
                <ListItem
                  key={header.key}
                  disablePadding
                  dense
                  title={isDisabled ? 'Mandatory column — cannot be hidden' : ''}
                  sx={{
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    backgroundColor: isDisabled
                      ? alpha(tokens.colors.neutral[500], 0.04)
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: isDisabled
                        ? alpha(tokens.colors.neutral[500], 0.04)
                        : alpha(tokens.colors.primary[500], 0.05),
                    },
                  }}
                >
                  <FormControlLabel
                    sx={{
                      m: 0,
                      width: '100%',
                      px: 1.5,
                      py: 0.75,
                      pointerEvents: isDisabled ? 'none' : 'auto',
                    }}
                    onChange={() => !isDisabled && handleToggle(header.key)}
                    control={
                      <Checkbox
                        checked={selectedKeys?.includes(header.key) ?? false}
                        disabled={isDisabled}
                        size="small"
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDisabled ? 'text.disabled' : 'text.primary',
                          ml: 0.5,
                        }}
                      >
                        {header.label}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
          {defaultKeys && (
            <>
              <Divider sx={{ borderStyle: 'dashed' }} />
              <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
                <Button
                  size="small"
                  color="inherit"
                  onClick={handleReset}
                  startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main', backgroundColor: 'transparent', textDecoration: 'underline' }
                  }}
                >
                  Reset to default
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default TableColumnManager;